import React, { useContext, useCallback } from 'react';
import { Slider, Stack } from '@mui/material';
import { styled } from '@mui/system';
import BrightnessHighIcon from '@mui/icons-material/BrightnessHigh';
import BrightnessLowIcon from '@mui/icons-material/BrightnessLow';

import { useAspenSocket } from 'hooks/useAspenSocket';
import { LeafStateContext } from 'context/state/leaf';
import { LightState } from 'types/aspen/state';
import { getColorProperties, getContrastingText, getContrastAnalysis } from 'modules/color';

// Brightness slider gradient constants
const BRIGHTNESS_TRACK_GRADIENT = 'linear-gradient(90deg, rgba(0, 0, 0, 0.2) 0%, rgba(245, 245, 245, 0.3) 100%)';

const BrightnessSliderMUI = styled(Slider, {
  shouldForwardProp: (prop) => !['preferLight', 'powerState', 'isGroup'].includes(prop as string),
})<{ preferLight: boolean; powerState: string; isGroup?: boolean }>(({ preferLight, powerState, isGroup }) => {
  const isPowerOff = powerState === 'off';
  const shouldUseLightStyling = preferLight || isPowerOff || isGroup;

  const getBoxShadow = () => {
    if (shouldUseLightStyling) return 'inset 0 2px 6px rgba(255, 255, 255, 0.5)';
    return '0 2px 6px rgba(0, 0, 0, 0.3)';
  };

  const getRailBackgroundColor = () => {
    if (shouldUseLightStyling) return 'rgba(255, 255, 255, 0.55)';
    return 'rgba(0, 0, 0, 0.35)';
  };

  const getLabelColor = () => {
    if (shouldUseLightStyling) return '#f5f5f5';
    return '#000';
  };

  return {
    height: 16,
    '& .MuiSlider-track': {
      background: BRIGHTNESS_TRACK_GRADIENT,
      borderRadius: 10,
      border: 'none',
      boxShadow: getBoxShadow(),
      transition: 'box-shadow 0.6s ease-in-out',
    },
    '& .MuiSlider-thumb': {
      backgroundColor: '#f5f5f5',
      height: 24,
      width: 24,
      boxShadow: '0 0 10px rgba(255, 255, 255, 0.6)',
      transition: 'opacity 0.3s ease-in-out',
      opacity: 0,
      '&:hover, &:focus, &:active': {
        opacity: 1,
      },
      '&:focus': {
        boxShadow: 'none',
        outline: 'none',
      },
    },
    '& .MuiSlider-rail': {
      backgroundColor: getRailBackgroundColor(),
      borderRadius: 10,
      boxShadow: 'inset 0 4px 8px rgba(0, 0, 0, 0.3)',
      transition: 'background-color 0.6s ease-in-out',
    },
    '& .MuiSlider-valueLabel': {
      top: -2,
      color: getLabelColor(),
      fontSize: 14,
      fontWeight: 'bold',
      backgroundColor: 'transparent',
      transition: 'color 0.6s ease-in-out',
    },
  };
});

interface BrightnessSliderProps {
  isGroup?: boolean | undefined;
}
export const BrightnessSlider = ({ isGroup }: BrightnessSliderProps) => {
  const { leaf, dispatch } = useContext(LeafStateContext);
  const { sendCommand } = useAspenSocket();

  const leafState = leaf.state as LightState;
  const brightnessLevel = leafState?.brightness?.level || 0;

  const { colorRgb, brightnessFactor, powerState, colorType, temperatureState } = getColorProperties(
    leafState,
    brightnessLevel,
  );

  const { preferLight } = getContrastAnalysis(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState);

  const handleBrightnessChange = useCallback(
    (event: Event) => {
      const reactEvent = event as unknown as React.ChangeEvent<HTMLInputElement>;
      const level = parseInt(reactEvent?.target?.value, 10);

      // Centralized debounced dispatch - immediate optimistic update + debounced command
      dispatch({ type: 'SET_BRIGHTNESS', payload: level, origin: { type: leaf.type, trigger: 'user' } }, () =>
        sendCommand({ leaf, command: { command: 'brightness', parameters: { level } } }),
      );
    },
    [dispatch, sendCommand, leaf],
  );

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
      <BrightnessLowIcon
        fontSize="medium"
        sx={{
          color:
            powerState === 'off' || isGroup
              ? '#fff'
              : getContrastingText(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState),
          fontSize: '26px',
        }}
      />
      <BrightnessSliderMUI
        preferLight={preferLight}
        powerState={powerState === 'on' ? 'on' : 'off'}
        isGroup={isGroup}
        getAriaLabel={() => 'Brightness level'}
        aria-label={`${leaf.name || 'Light'} brightness: ${brightnessLevel}%`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={brightnessLevel}
        aria-valuetext={`${brightnessLevel} percent brightness`}
        value={brightnessLevel}
        min={0}
        max={100}
        valueLabelDisplay="on"
        onChange={handleBrightnessChange}
      />
      <BrightnessHighIcon
        fontSize="medium"
        sx={{
          color:
            powerState === 'off' || isGroup
              ? '#fff'
              : getContrastingText(colorRgb, brightnessFactor, brightnessLevel, colorType, temperatureState),
          fontSize: '26px',
        }}
      />
    </Stack>
  );
};
